import { apiClient } from "./api";

export const applicationService = {
  importGoogleFormCandidates: (job_id) =>
    apiClient.post(`/applications/import-google-form/${job_id}`, {}),

  // Erase a candidate from this job. Not the same as rejecting — reject keeps
  // the record and the reason, this removes that they were ever here. The
  // person themselves survives if they are in another job.
  deleteApplication: (application_id) =>
    apiClient.delete(`/applications/${application_id}`),

  getApplicationsByJob: (job_id) =>
    apiClient.get(`/applications/job/${job_id}`),

  getApplicationById: (application_id) =>
    apiClient.get(`/applications/${application_id}`),

  scoreScreening: (application_id, screening_score, remarks) =>
    apiClient.post("/applications/score-screening", {
      application_id,
      screening_score,
      remarks,
    }),

  moveToNextStage: (application_id, next_stage_id, remarks) =>
    apiClient.put(`/applications/move-stage/${application_id}`, {
      next_stage_id,
      remarks,
    }),

  /**
   * Move many candidates into one round at once.
   *
   * The server refuses an AI_INTERVIEW target, deliberately: moving someone
   * there also issues a signed link, spends interview credit and sends an
   * email, and each of those can fail for a reason that names one candidate.
   * The pipeline loops individual invites for that round instead.
   */
  bulkMoveStage: (application_ids, next_stage_id, remarks) =>
    apiClient.put(`/applications/bulk-move-stage`, {
      application_ids,
      next_stage_id,
      remarks,
    }),

  /**
   * Reject many at once.
   *
   * `data.reverted` comes back with the ids that actually changed — not the
   * whole selection. Replaying the selection to undo would also un-reject
   * anyone who was already out before the bulk action ran.
   */
  bulkReject: (application_ids, remarks) =>
    apiClient.put(`/applications/bulk-reject`, { application_ids, remarks }),

  rejectApplication: (application_id, remarks) =>
    apiClient.put(`/applications/reject/${application_id}`, { remarks }),

  /** Puts a rejected candidate back in the pipeline as APPLIED. */
  unrejectApplication: (application_id) =>
    apiClient.put(`/applications/unreject/${application_id}`),

  // ── The operator's own record of what they did ──────────────────────────
  //
  // `remarks` on the two reject calls above is now COMPULSORY on the server:
  // it refuses with 400 and an explanation if it is missing, whitespace, or a
  // single character. Both used to be called with "" from the UI, and the
  // server substituted "Candidate rejected" — a placeholder that reads in the
  // audit trail exactly like a reason somebody typed.
  //
  // A note on its own, attached to no other action. "Spoke to her, she is on
  // notice until the 20th" is the thing every pipeline needs and this one had
  // no way to record. NOTE_ADDED has been in the activity_type enum since
  // migration 009 with nothing able to write it.
  addNote: (application_id, note) =>
    apiClient.post(`/applications/${application_id}/notes`, { note }),

  /**
   * The full audit trail for one candidate: every action, its note, and the
   * operator who did it.
   *
   * NOT the same as the short `timeline` the screening list attaches to every
   * row — that one is a summary built for four hundred rows at once, drops
   * NOTE_ADDED, and carries no operator. This is fetched for one candidate
   * somebody has opened.
   */
  activityTrail: (application_id) =>
    apiClient.get(`/applications/${application_id}/activity`),

  updateStageScore: (application_id, { scoreField, score, remarks }) =>
    apiClient.patch(`/applications/${application_id}/score`, {
      scoreField,
      score,
      remarks,
    }),
  completeApplication: (application_id, remarks) =>
    apiClient.put(`/applications/${application_id}/complete`, { remarks }),
};
